import { useUnmount } from 'react-use';
import { devtools } from 'zustand/middleware';

import { createStoreWithReset } from '@/shared/lib/zustand';

import type { RequirementRowData } from './types.ts';

type RequirementStore = {
  isFiltersPanelVisible: boolean;
  isRequirementSettingsDialogVisible: boolean;
  isRequirementResetDialogVisible: boolean;

  searchQuery: string;
  selectedRow: RequirementRowData | null;

  isZeroCountProducts: boolean;

  actions: {
    openFiltersPanel: () => void;
    closeFiltersPanel: () => void;

    toggleZeroCountProducts: () => void;

    openRequirementSettingsDialog: () => void;
    closeRequirementSettingDialog: () => void;

    openRequirementResetDialog: () => void;
    closeRequirementResetDialog: () => void;

    setSearchQuery: (query: string) => void;
    resetSearchQuery: () => void;

    setSelectedRow: (selectedRow: RequirementRowData | null) => void;
  };
};

const [useRequirementStore, resetRequirementStore] = createStoreWithReset<RequirementStore>()(
  devtools(
    (set, get) => ({
      isFiltersPanelVisible: false,
      isRequirementSettingsDialogVisible: false,
      isRequirementResetDialogVisible: false,

      searchQuery: '',

      selectedRow: null,

      isZeroCountProducts: false,

      actions: {
        openFiltersPanel: () => set({ isFiltersPanelVisible: true }, false, 'openFiltersPanel'),
        closeFiltersPanel: () => set({ isFiltersPanelVisible: false }, false, 'closeFiltersPanel'),

        toggleZeroCountProducts: () =>
          set(
            {
              isZeroCountProducts: !get().isZeroCountProducts,
            },
            false,
            'toggleZeroCountProducts',
          ),

        openRequirementSettingsDialog: () =>
          set({ isRequirementSettingsDialogVisible: true }, false, 'openRequirementSettingsDialog'),
        closeRequirementSettingDialog: () =>
          set({ isRequirementSettingsDialogVisible: false }, false, 'closeRequirementSettingDialog'),

        openRequirementResetDialog: () =>
          set({ isRequirementResetDialogVisible: true }, false, 'openRequirementResetDialog'),
        closeRequirementResetDialog: () =>
          set({ isRequirementResetDialogVisible: false }, false, 'closeRequirementResetDialog'),

        setSearchQuery: searchQuery => set({ searchQuery }, false, 'setSearchQuery'),
        resetSearchQuery: () => set({ searchQuery: '' }, false, 'resetSearchQuery'),

        setSelectedRow: selectedRow => set({ selectedRow }, false, 'setSelectedRow'),
      },
    }),
    {
      name: 'requirement-store',
      enabled: import.meta.env.DEV,
    },
  ),
);

// Hooks
export const useRequirementStoreFiltersPanelState = () => useRequirementStore(state => state.isFiltersPanelVisible);
export const useRequirementStoreIsZeroCountProducts = () => useRequirementStore(state => state.isZeroCountProducts);

export const useRequirementStoreSettingsDialogState = () =>
  useRequirementStore(state => state.isRequirementSettingsDialogVisible);
export const useRequirementStoreResetDialogState = () =>
  useRequirementStore(state => state.isRequirementResetDialogVisible);

export const useRequirementStoreSearchQuery = () => useRequirementStore(state => state.searchQuery);

export const useRequirementStoreSelectedRow = () => useRequirementStore(state => state.selectedRow);

export const useRequirementStoreActions = () => useRequirementStore(state => state.actions);
export const useRequirementStoreResetOnUnmount = () => useUnmount(resetRequirementStore);
